import { useEffect, useRef, useCallback } from 'react';
import { useUIStore } from '@/stores/uiStore';
import { useMapStore } from '@/stores/mapStore';
import { getTripPosition } from '@/api/routes';

const POLL_INTERVAL_MS = 30_000; // 30 segundos
const DEVIATION_COOLDOWN_MS = 120_000; // 2 minutos entre callbacks de desvío

export function useLivePosition(
  alias: string | null,
  onDeviation?: () => void,
) {
  const routePanelOpen = useUIStore((s) => s.routePanelOpen);
  const setLivePosition = useMapStore((s) => s.setLivePosition);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastEventRef = useRef<string | null>(null);
  const lastDeviationCallRef = useRef<number>(0);

  const fetchPosition = useCallback(async () => {
    if (!alias) return;
    try {
      const data = await getTripPosition(alias);
      if (!data.success) {
        setLivePosition(null); // No hay posición para este viaje
        return;
      }

      // Ignorar posiciones con timestamp más viejo que la última recibida
      if (data.last_event && lastEventRef.current) {
        if (data.last_event < lastEventRef.current) return;
      }
      if (data.last_event) lastEventRef.current = data.last_event;

      setLivePosition(data);

      // Detectar desvío con cooldown de 2 minutos
      if (data.off_route === true && onDeviation) {
        const now = Date.now();
        if (now - lastDeviationCallRef.current > DEVIATION_COOLDOWN_MS) {
          lastDeviationCallRef.current = now;
          onDeviation();
        }
      }
    } catch {
      // Silenciar errores de polling
    }
  }, [alias, setLivePosition, onDeviation]);

  useEffect(() => {
    if (!routePanelOpen || !alias) {
      setLivePosition(null);
      lastEventRef.current = null;
      return;
    }

    // Reset al cambiar de viaje
    lastEventRef.current = null;
    lastDeviationCallRef.current = 0;

    // No anular livePosition aquí — mantener el ícono del camión anterior
    // visible mientras se carga la posición del nuevo viaje.
    // fetchPosition lo reemplazará o lo anulará si no hay datos.
    fetchPosition();

    // Poll cada 30s
    intervalRef.current = setInterval(fetchPosition, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      // No anular livePosition en cleanup — el siguiente efecto o el
      // early return (panel cerrado) se encarga de eso.
    };
  }, [routePanelOpen, alias, fetchPosition, setLivePosition]);

  // Limpiar livePosition al desmontar el componente
  useEffect(() => {
    return () => { setLivePosition(null); };
  }, [setLivePosition]);
}
